import { useState, useEffect, useCallback } from "react";
import { TrendUp, ArrowClockwise } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ToolShell } from "@/components/ToolShell";
import { CopyButton } from "@/components/CopyButton";
import { useSeo } from "@/lib/seo";

export default function TrendingTags() {
  useSeo("Trending YouTube Tags", "The most-extracted YouTube tags across all VidhFlow creators — spot rising topics before they peak.");
  const [tags, setTags] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/trending-tags");
      setTags(res.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const maxCount = tags?.[0]?.count || 1;

  return (
    <ToolShell slug="trending-tags" title="Trending Tags" tagline="Every tag extracted on VidhFlow feeds this board. The bigger the chip, the hotter the topic." hasResults={!!tags?.length}>
      <div className="surface rounded-[20px] p-6 sm:p-8">
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm font-semibold flex items-center gap-2">
            <TrendUp size={18} color="var(--coral)" weight="duotone" /> Top {tags?.length || 0} tags right now
          </p>
          <button
            data-testid="trending-refresh-btn"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full surface text-xs font-semibold hover:border-[var(--glow)] transition-colors disabled:opacity-60"
          >
            <ArrowClockwise size={13} weight="bold" className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
        {tags && tags.length === 0 && (
          <p className="text-[var(--muted)] text-sm py-4" data-testid="trending-empty">
            No tags tracked yet. Run the <Link to="/tools/tag-extractor" className="text-[var(--glow)] hover:underline">Tag Extractor</Link> on a few videos to seed the board.
          </p>
        )}
        {tags && tags.length > 0 && (
          <>
            <div className="flex flex-wrap gap-2.5 items-center" data-testid="trending-tags-list">
              {tags.map((t, i) => {
                const ratio = t.count / maxCount;
                const hot = i < 3;
                return (
                  <span
                    key={t.tag}
                    className="inline-flex items-center gap-2 rounded-full border px-4 py-1.5"
                    style={{
                      fontSize: `${0.8 + ratio * 0.35}rem`,
                      background: hot ? "color-mix(in srgb, var(--coral) 12%, transparent)" : "color-mix(in srgb, var(--glow) 10%, transparent)",
                      borderColor: hot ? "color-mix(in srgb, var(--coral) 35%, transparent)" : "color-mix(in srgb, var(--glow) 30%, transparent)",
                    }}
                  >
                    {t.tag}
                    <span className="text-xs font-bold" style={{ color: hot ? "var(--coral)" : "var(--glow)" }}>{t.count}</span>
                  </span>
                );
              })}
            </div>
            <div className="mt-6">
              <CopyButton text={tags.map((t) => t.tag).join(", ")} label="Copy all trending tags" testId="copy-trending-tags-btn" />
            </div>
          </>
        )}
      </div>
    </ToolShell>
  );
}
